import { useCallback, useEffect, useRef, useState } from "react";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import { formatUkDateTime } from "../lib/dates.js";
import { type BundleVerification, verifyBundleInBrowser } from "../lib/verifyBundleInBrowser.js";

interface ContentBundle {
  id: string;
  url: string;
  verification_status: string;
  bypassed: boolean;
  bypass_reason?: string | null;
  set_by_email?: string | null;
  created_at: string;
}

interface ContentBundleResponse {
  current?: ContentBundle;
  effective_source: string;
  env_fallback_url: string | null;
  device_override_count: number;
  affected_device_count: number;
  history: ContentBundle[];
}

const inputClass =
  "w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary focus:outline-none focus:border-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

function passed(v: BundleVerification): boolean {
  return v.loads && v.exposes_global && v.implements_init;
}

function CheckRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      {/* aria-label makes each verdict addressable — the e2e spec counts passes. */}
      <span aria-label={ok ? "pass" : "fail"} className={ok ? "text-green-400" : "text-red-400"}>
        {ok ? "✓" : "✕"}
      </span>
      <span className="text-text-secondary">{label}</span>
    </li>
  );
}

export function ContentBundle() {
  const { user } = useAuth();
  const [data, setData] = useState<ContentBundleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [url, setUrl] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verification, setVerification] = useState<BundleVerification | null>(null);
  const [saving, setSaving] = useState(false);

  const [bypassStage, setBypassStage] = useState<0 | 1 | 2>(0);
  const [bypassTyped, setBypassTyped] = useState("");
  const [bypassReason, setBypassReason] = useState("");

  // Tracks the URL input's live value so an in-flight verification can tell,
  // once it resolves, whether the field it was checking is still what's on
  // screen — the check can take up to 15s, long enough for the operator to
  // have typed a different URL in the meantime.
  const urlRef = useRef(url);
  useEffect(() => {
    urlRef.current = url;
  }, [url]);

  const load = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/admin/content-bundle"]["get"]({
        parameters: {},
        body: null,
      });
      if (res.code !== 200) {
        setError("Could not load the content bundle settings.");
        return;
      }
      setData(res.body as ContentBundleResponse);
      setError(null);
    } catch {
      setError("Could not reach the server to load the content bundle settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // A changed URL invalidates any verdict already on screen.
  const changeUrl = (next: string) => {
    setUrl(next);
    setVerification(null);
  };

  const runVerification = async () => {
    const target = url.trim();
    setVerifying(true);
    setError(null);
    setVerification(null);
    try {
      const result = await verifyBundleInBrowser(target);
      // Only apply the verdict if the field still holds the URL it was checking —
      // otherwise a verdict for the old URL could land on whatever's typed now.
      if (urlRef.current.trim() === target) {
        setVerification(result);
      }
    } finally {
      setVerifying(false);
    }
  };

  const save = async (options: { bypass?: boolean } = {}) => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiClient["/api/v1/admin/content-bundle"]["post"]({
        parameters: {},
        body: (options.bypass
          ? { url: url.trim(), bypass: true, bypass_reason: bypassReason.trim() }
          : { url: url.trim(), verification: verification ?? undefined }) as never,
      });
      if (res.code !== 200) {
        setError(
          (res.body as { message?: string })?.message ?? "Could not save the content bundle.",
        );
        return;
      }
      setData(res.body as ContentBundleResponse);
      setUrl("");
      setVerification(null);
      closeBypass();
    } catch {
      setError("Could not reach the server to save the content bundle.");
    } finally {
      setSaving(false);
    }
  };

  const closeBypass = () => {
    setBypassStage(0);
    setBypassTyped("");
    setBypassReason("");
  };

  const rollBackTo = async (bundle: ContentBundle) => {
    const target = bundle.url;
    setUrl(target);
    setVerification(null);
    setVerifying(true);
    try {
      const result = await verifyBundleInBrowser(target);
      // Same guard as runVerification: only apply the verdict if the field
      // still holds this bundle's URL when the check comes back.
      if (urlRef.current.trim() === target.trim()) {
        setVerification(result);
      }
    } finally {
      setVerifying(false);
    }
  };

  if (!user?.is_platform_admin) {
    return (
      <div className="p-6">
        <p className="text-text-secondary">
          Only TeamTable platform admins can change the games content bundle.
        </p>
      </div>
    );
  }

  if (loading) {
    return <div className="p-6 text-text-muted">Loading…</div>;
  }

  const canSave = Boolean(url.trim()) && Boolean(verification && passed(verification));

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-xl font-bold text-text-primary mb-1">Games Content Bundle</h1>
      <p className="text-sm text-text-muted mb-6">
        The JavaScript bundle every table loads. Changes apply the next time a table loads the
        content app.
      </p>

      {error && (
        <div className="mb-4 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Current */}
      <section className="mb-6 border border-border rounded-md p-4">
        <h2 className="text-sm text-text-secondary mb-2">Current</h2>
        {!data ? (
          <p className="text-sm text-red-400">
            Could not load the current bundle status — state unknown.
          </p>
        ) : data.current ? (
          <>
            <p className="font-mono text-sm text-text-primary break-all">{data.current.url}</p>
            <p className="text-xs text-text-muted mt-1">
              Set by {data.current.set_by_email ?? "an unknown user"} on{" "}
              {formatUkDateTime(data.current.created_at)} ·{" "}
              {data.current.verification_status === "verified" ? "verified" : "not verified"}
            </p>
            {data.current.bypassed && (
              <div className="mt-3 px-3 py-2 rounded-md bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-sm">
                Saved without verification. Reason: {data.current.bypass_reason}
              </div>
            )}
          </>
        ) : data.env_fallback_url ? (
          <>
            <p className="font-mono text-sm text-text-primary break-all">{data.env_fallback_url}</p>
            <p className="text-xs text-text-muted mt-1">
              No bundle has been set here — tables are falling back to the deploy-time
              CONTENT_BUNDLE_URL.
            </p>
          </>
        ) : (
          <p className="text-sm text-red-400">
            No bundle is configured. Tables have nothing to load.
          </p>
        )}
        {data && (
          <p className="text-xs text-text-muted mt-3">
            Applies to {data.affected_device_count} deployed{" "}
            {data.affected_device_count === 1 ? "table" : "tables"}.{" "}
            {data.device_override_count > 0 && (
              <>
                <a href="/devices" className="text-accent hover:underline">
                  {data.device_override_count} table
                  {data.device_override_count === 1 ? "" : "s"} override
                  {data.device_override_count === 1 ? "s" : ""} this
                </a>{" "}
                and will not change.
              </>
            )}
          </p>
        )}
      </section>

      {/* Set a new bundle */}
      <section className="mb-6 border border-border rounded-md p-4">
        <h2 className="text-sm text-text-secondary mb-2">Set a new bundle</h2>
        <input
          type="text"
          value={url}
          onChange={(e) => changeUrl(e.target.value)}
          placeholder="https://cdn.example.com/bundle.js"
          disabled={verifying}
          className={`${inputClass} font-mono text-sm`}
        />

        {verification && (
          <ul className="mt-3 space-y-1">
            <CheckRow ok={verification.loads} label="Loads from that URL" />
            <CheckRow ok={verification.exposes_global} label="Sets window.teamtableContent" />
            <CheckRow ok={verification.implements_init} label="Implements init()" />
            <CheckRow ok={verification.has_destroy} label="Implements destroy() (optional)" />
            {verification.error && (
              <li className="text-sm text-red-400 mt-2">{verification.error}</li>
            )}
          </ul>
        )}

        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={runVerification}
            disabled={!url.trim() || verifying}
            className="px-4 py-2 text-sm rounded-md border border-border text-text-primary hover:bg-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {verifying ? "Verifying…" : "Verify"}
          </button>
          <button
            onClick={() => void save()}
            disabled={!canSave || saving}
            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            onClick={() => setBypassStage(1)}
            disabled={!url.trim() || saving}
            className="px-4 py-2 text-sm rounded-md border border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save without verification
          </button>
        </div>
      </section>

      {/* History */}
      <section className="border border-border rounded-md p-4">
        <h2 className="text-sm text-text-secondary mb-3">History</h2>
        {data && data.history.length > 0 ? (
          <ul className="space-y-2">
            {data.history.map((entry) => (
              <li
                key={entry.id}
                className="flex items-center justify-between gap-4 border-b border-border last:border-0 pb-2"
              >
                <div className="min-w-0">
                  <p className="font-mono text-xs text-text-primary break-all">{entry.url}</p>
                  <p className="text-xs text-text-muted">
                    {formatUkDateTime(entry.created_at)} · {entry.set_by_email ?? "unknown"}
                    {entry.bypassed && (
                      <span className="ml-2 text-yellow-300">bypassed verification</span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => void rollBackTo(entry)}
                  disabled={verifying || saving}
                  className="shrink-0 px-3 py-1 text-xs rounded-md border border-border text-text-secondary hover:text-text-primary hover:bg-surface transition-colors disabled:opacity-50"
                >
                  Roll back
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-text-muted">Nothing set yet.</p>
        )}
      </section>

      {/* Bypass confirmation 1 */}
      {bypassStage === 1 && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-6">
          <div className="bg-surface border border-border rounded-md p-5 max-w-md">
            <h3 className="text-text-primary font-medium mb-2">Skip verification?</h3>
            {error && (
              <div className="mb-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}
            <p className="text-sm text-text-secondary">
              This sets the games bundle on {data?.affected_device_count ?? 0} deployed{" "}
              {data?.affected_device_count === 1 ? "table" : "tables"} without checking that it
              loads. If it is wrong, those tables show nothing until you change it back.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={closeBypass}
                className="px-4 py-2 text-sm rounded-md border border-border text-text-primary hover:bg-surface transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setBypassStage(2)}
                className="px-4 py-2 text-sm rounded-md border border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10 transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bypass confirmation 2 */}
      {bypassStage === 2 && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-6">
          <div className="bg-surface border border-border rounded-md p-5 max-w-md w-full">
            <h3 className="text-text-primary font-medium mb-2">Confirm the bypass</h3>
            {error && (
              <div className="mb-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}
            <p className="font-mono text-xs text-text-muted break-all mb-3">{url.trim()}</p>
            <label htmlFor="bypass-confirm" className="block text-sm text-text-secondary mb-1">
              Type BYPASS to confirm
            </label>
            <input
              id="bypass-confirm"
              type="text"
              value={bypassTyped}
              onChange={(e) => setBypassTyped(e.target.value)}
              className={`${inputClass} mb-3`}
            />
            <label htmlFor="bypass-reason" className="block text-sm text-text-secondary mb-1">
              Why are you bypassing?
            </label>
            <textarea
              id="bypass-reason"
              value={bypassReason}
              onChange={(e) => setBypassReason(e.target.value)}
              rows={3}
              className={`${inputClass} resize-y`}
            />
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={closeBypass}
                className="px-4 py-2 text-sm rounded-md border border-border text-text-primary hover:bg-surface transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => void save({ bypass: true })}
                disabled={bypassTyped !== "BYPASS" || !bypassReason.trim() || saving}
                className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving…" : "Set without verifying"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
